import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTheme } from 'emotion-theming';
import {
  Page,
  Card,
  Select,
  TextInput,
  FormField,
  Button,
  InlineMessage,
  Flex,
  FileInput,
} from '../../components';
import {
  getCarrier, getClient,
} from '../../modules/selectors';
import api from '../../utils/services';
import { FileUploadTypes } from '../../utils/constants';


const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      carrierId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  carrier: PropTypes.shape({
    carrierId: PropTypes.number,
  }).isRequired,
  client: PropTypes.shape({
    clientName: PropTypes.string,
  }).isRequired,
  theme: PropTypes.shape({
    backgroundColor_warningPrimary_active: PropTypes.string.isRequired,
    backgroundColor_success_active: PropTypes.string.isRequired,
    backgroundColor_dangerPrimary_active: PropTypes.string.isRequired,
  }).isRequired,
};
function NewFile({ carrier: { carrierId }, client: { clientName }, theme }) {
  const currentYear = new Date().getFullYear();
  const planYears = [currentYear - 1, currentYear, currentYear + 1];
  const filterValueOptions = planYears.map(option => ({
    value: option.toString(),
    text: option.toString(),
  }));
  const imageTypes = [
    {
      text: FileUploadTypes.Logo,
      value: FileUploadTypes.Logo,
    },
    {
      text: FileUploadTypes.Plan,
      value: FileUploadTypes.Plan,
    },
    {
      text: FileUploadTypes.Banner,
      value: FileUploadTypes.Banner,
    },
    {
      text: `${FileUploadTypes.Advanced} (Let's you add the custom image name)`,
      value: FileUploadTypes.Advanced,
    },
  ];

  const [selectedFile, setSelectedFile] = useState(null);
  const [imageName, setImageName] = useState('');
  const [imageType, setImageType] = useState('');
  const [planYear, setPlanYear] = useState();
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [disablePlanYear, setDisablePlanYear] = useState(false);
  const [imageSizeWarning, setImageSizeWarning] = useState('');
  const handleCapture = ({ target }) => {
    setImageSizeWarning('');
    const selectedLogo = target.files[0];
    if (selectedLogo.size / 1024 > 50) {
      setImageSizeWarning('Warning: The selected logo exceeds 50KB in size, please consider compressing the image before uploading!');
    }
    setSelectedFile(target.files[0]);
  };

  const handleImagePath = () => {
    let path = '';
    if (imageName) {
      path = imageName;
    }
    if (imageType.value === FileUploadTypes.Advanced) {
      return path;
    }
    if (imageType) {
      path += `-${imageType.text}`;
    }
    if (planYear) {
      path += `-${planYear.text}`;
    }
    return path;
  };

  const handleGetFileExtension = () => selectedFile.type.replace(/(.*)\//g, '');

  const validateForm = () => {
    const message = [];
    let isValid = true;
    if (!imageName) {
      message.push('Image Name is required!');
      isValid = false;
    }
    if (!imageType) {
      message.push('Image Type is required!');
      isValid = false;
    }
    if (!planYear && imageType.value !== FileUploadTypes.Advanced) {
      message.push('Plan year is required!');
      isValid = false;
    }
    if (!selectedFile) {
      message.push('Image file is required!');
      isValid = false;
    }
    setErrorMessage(message);
    return isValid;
  };

  const UploadFile = (file, filePath) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filePath', `${filePath}`);
    api.uploadFile(formData)
      .then((response) => {
        if (response && response.httpStatusCode === 200) {
          setSuccessMessage(`${filePath} uploaded successfully!`);
          setSaving(false);
        }
      }).catch((err) => {
        setErrorMessage(`Something went wrong! Reason: ${err.message}`);
        setSaving(false);
      }).finally(() => {
        setImageSizeWarning('');
      });
  };

  const handleSubmit = () => {
    // remove if there are any status messages from previous requests.
    setSuccessMessage(null);
    setErrorMessage(null);
    if (!validateForm()) {
      return;
    }
    setSaving(true);
    const fileName = handleImagePath();
    const fileExtension = handleGetFileExtension();
    if (imageType.value === FileUploadTypes.Advanced) {
      UploadFile(selectedFile, `${clientName}/${carrierId}/${fileName}.${fileExtension}`);
    } else {
      api.computeFilePath(clientName, carrierId, fileName, fileExtension).then((filePath) => {
        UploadFile(selectedFile, filePath);
      });
    }
  };
  const handleSetImageType = (e) => {
    if (e.value === FileUploadTypes.Advanced) {
      setDisablePlanYear(true);
    } else {
      setDisablePlanYear(false);
    }
    setImageType(e);
  };

  return (
    <Page
      breadcrumbs={[{ content: 'Images', href: `/carriers/${carrierId}/staticFiles` }]}
      title="Image Uploads"
    >
      <Card>
        <Card.Block>
          <FormField
            label="Image Name: "
            input={TextInput}
            value={imageName}
            onChange={({ target: { value } }) => setImageName(value)}
          />
          <FormField
            label="Image Type: "
            name="ImageType"
            input={Select}
            data={imageTypes}
            onChange={e => handleSetImageType(e)}
          />
          <FormField
            label="Plan Year: "
            data={filterValueOptions}
            input={Select}
            onChange={e => setPlanYear(e)}
            disabled={disablePlanYear}
          />
          <br />
          <FileInput
            accept="image/*"
            handleCapture={handleCapture}
            selectedFile={selectedFile}
          >
            Choose an Image
          </FileInput>
          <Flex paddingTop="1em">
            <Flex.Item marginStart="auto">
              <Button primary loading={saving} onClick={handleSubmit}>Upload Image</Button>
            </Flex.Item>
          </Flex>
          <InlineMessage message={imageSizeWarning} align="center" color={theme.backgroundColor_warningPrimary_active} fieldId="image" />
          <InlineMessage message={successMessage} color={theme.backgroundColor_success_active} fieldId="image" />
          <InlineMessage message={errorMessage} color={theme.backgroundColor_dangerPrimary_active} fieldId="image" />
        </Card.Block>
      </Card>
    </Page>

  );
}

function mapStateToProps(state) {
  return {
    carrier: getCarrier(state),
    client: getClient(state),
  };
}

NewFile.propTypes = propTypes;
export default withTheme(connect(mapStateToProps)(NewFile));
